/**
 * "View as" profile for the logged-in user (session unchanged).
 * Primary account stays in localStorage `userData`; this overlay lives in sessionStorage.
 */
import { store } from "../store";
import { apiSlice } from "./apiSlice";

export const ACTIVE_PROFILE_STORAGE_KEY = "activeProfileContext";

/** RTK Query: refetch patient/user-scoped data after profile switch */
export function invalidateApiCacheForActiveProfile() {
  try {
    store.dispatch(
      apiSlice.util.invalidateTags([
        "Patients",
        "Appointments",
        "FamilyMembers",
        "dashboardData",
        "chat-summary",
        "Allergies",
        "medication",
        "chat",
        "Items",
        "ProfileSwitch",
        "Vitals",
      ])
    );
  } catch {
    /* ignore if store not ready */ 
  }
}

export type ActiveProfileContext =
  | { mode: "self" }
  | {
      mode: "related";
      relatedUserId: string;
      patientId: string | null;
      firstName?: string;
      middleName?: string;
      lastName?: string;
      gender?: string;
    };

export function readPrimaryUserData(): Record<string, any> {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function readActiveProfile(): ActiveProfileContext {
  try {
    const raw = sessionStorage.getItem(ACTIVE_PROFILE_STORAGE_KEY);
    if (!raw) return { mode: "self" };
    const p = JSON.parse(raw);
    if (p?.mode === "related" && p.relatedUserId) {
      return {
        mode: "related",
        relatedUserId: String(p.relatedUserId),
        patientId:
          p.patientId === undefined || p.patientId === null || p.patientId === ""
            ? null
            : String(p.patientId),
        firstName: p.firstName,
        middleName: p.middleName,
        lastName: p.lastName,
        gender: p.gender,
      };
    }
  } catch {
    /* ignore */
  }
  return { mode: "self" };
}

/** Merge primary login user with active "view as" overlay (for APIs / UI that follow patient). */
export function getEffectiveUserData(): Record<string, any> {
  const base = readPrimaryUserData();
  const ap = readActiveProfile();
  if (ap.mode === "self") return { ...base };

  // Related profile: never fall back to primary user's patientId (pending questionnaire = no id).
  const rawPid = ap.patientId;
  const relatedPatientId =
    rawPid != null &&
    rawPid !== "" &&
    String(rawPid).toLowerCase() !== "null" &&
    String(rawPid).toLowerCase() !== "undefined"
      ? String(rawPid)
      : null;

  return {
    ...base,
    patientId: relatedPatientId,
    userId: ap.relatedUserId,
    firstName: ap.firstName ?? base.firstName,
    middleName: ap.middleName ?? base.middleName,
    lastName: ap.lastName ?? base.lastName,
    gender: ap.gender ?? base.gender,
    /** True when dashboard/APIs should use effective ids but relationship list must use primary */
    _isViewingRelatedProfile: true,
    _relatedUserId: ap.relatedUserId,
  };
}

export function clearActiveProfile() {
  sessionStorage.removeItem(ACTIVE_PROFILE_STORAGE_KEY);
  invalidateApiCacheForActiveProfile();
  window.dispatchEvent(new Event("activeProfileChanged"));
}

export function setActiveProfileRelated(member: Record<string, any>) {
  const relatedUserId = String(
    member.relatedUserId ?? member.memberId ?? member.userId ?? ""
  );
  if (!relatedUserId) return;

  const patientRaw = member.patientId;
  const patientId =
    patientRaw === undefined || patientRaw === null || patientRaw === ""
      ? null
      : String(patientRaw);

  const ctx: ActiveProfileContext = {
    mode: "related",
    relatedUserId,
    patientId,
    firstName: member.firstName,
    middleName: member.middleName,
    lastName: member.lastName,
    gender: member.gender,
  };
  sessionStorage.setItem(ACTIVE_PROFILE_STORAGE_KEY, JSON.stringify(ctx));
  invalidateApiCacheForActiveProfile();
  window.dispatchEvent(new Event("activeProfileChanged"));
}

export function setActiveProfileSelf() {
  clearActiveProfile();
}

export function patchActiveRelatedPatientId(patientId: string | number | null) {
  const cur = readActiveProfile();
  if (cur.mode !== "related") return;
  const next: ActiveProfileContext = {
    ...cur,
    patientId:
      patientId === undefined || patientId === null || patientId === ""
        ? null
        : String(patientId),
  };
  sessionStorage.setItem(ACTIVE_PROFILE_STORAGE_KEY, JSON.stringify(next));
  invalidateApiCacheForActiveProfile();
  window.dispatchEvent(new Event("activeProfileChanged"));
}
